import React, { useState, useRef, useEffect } from 'react';
import {
  Plus,
  Box,
  Image,
  FolderOpen,
  Type,
  Brush,
  Bone,
} from 'lucide-react';
import { GameObjectType } from '../../../core/types/gameobject.types';
import { useSceneStore } from '../../store/sceneStore';
import { cn } from '../../utils/cn';

interface CreateDropdownProps {
  parentId?: string | null;
  onClose?: () => void;
}

interface ObjectTypeOption {
  type: GameObjectType;
  label: string;
  icon: React.ReactNode;
  description: string;
}

const OBJECT_TYPE_OPTIONS: ObjectTypeOption[] = [
  {
    type: 'empty',
    label: 'Empty Object',
    icon: <Box className="w-4 h-4" />,
    description: 'Empty container for scripts and components',
  },
  {
    type: 'sprite',
    label: 'Sprite',
    icon: <Image className="w-4 h-4" />,
    description: '2D image or texture',
  },
  {
    type: 'container',
    label: 'Container',
    icon: <FolderOpen className="w-4 h-4" />,
    description: 'Group to organize objects',
  },
  {
    type: 'text',
    label: 'Text',
    icon: <Type className="w-4 h-4" />,
    description: 'Text display element',
  },
  {
    type: 'graphics',
    label: 'Graphics',
    icon: <Brush className="w-4 h-4" />,
    description: 'Vector shapes and drawings',
  },
  {
    type: 'spine',
    label: 'Spine Object',
    icon: <Bone className="w-4 h-4" />,
    description: 'Spine skeletal animation',
  },
];

export const CreateDropdown: React.FC<CreateDropdownProps> = ({
  parentId = null,
  onClose,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const createGameObject = useSceneStore((state) => state.createGameObject);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        onClose?.();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const handleCreate = (type: GameObjectType) => {
    createGameObject(type, parentId);
    setIsOpen(false);
    onClose?.();
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={toggleDropdown}
        className={cn(
          'p-1.5 rounded hover:bg-zinc-700 transition-colors',
          isOpen && 'bg-zinc-700'
        )}
        title="Create new object"
      >
        <Plus className="w-4 h-4 text-zinc-400 hover:text-zinc-200" />
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute left-0 top-full mt-1 w-56 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-50 overflow-hidden"
        >
          <div className="px-3 py-2 border-b border-zinc-700">
            <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">
              Create Object
            </span>
          </div>
          <div className="py-1 max-h-80 overflow-y-auto">
            {OBJECT_TYPE_OPTIONS.map((option) => (
              <button
                key={option.type}
                onClick={() => handleCreate(option.type)}
                className="w-full px-3 py-2 flex items-start gap-3 hover:bg-zinc-700 transition-colors text-left"
              >
                <span className="text-zinc-400 mt-0.5">{option.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-200">{option.label}</div>
                  <div className="text-xs text-zinc-500 truncate">
                    {option.description}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
